import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSessions, getTerms, getClasses, getClassResults,
  computeResults, approveResults, publishResults,
} from '@/api/owner';
import {
  Calculator, ShieldCheck, Send, AlertCircle, Loader2, CheckCircle2,
  ChevronRight, Users, X,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

type Stage = 'compute' | 'approve' | 'publish';

const statusTone = (status: string) => {
  const map: Record<string, string> = {
    draft:     'bg-slate-100 text-slate-700 border-slate-200',
    computed:  'bg-blue-50 text-blue-700 border-blue-200',
    approved:  'bg-violet-50 text-violet-700 border-violet-200',
    published: 'bg-green-50 text-green-700 border-green-200',
    held:      'bg-amber-50 text-amber-800 border-amber-200',
  };
  return map[status] ?? 'bg-slate-100 text-slate-600 border-slate-200';
};

export function PublishPage() {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('compute');
  const [lastResult, setLastResult] = useState<{ stage: Stage; data: any } | null>(null);

  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });

  const sessions = arr<any>(sessionsQ.data);
  const terms = arr<any>(termsQ.data);
  const classes = arr<any>(classesQ.data);

  const visibleTerms = useMemo(
    () => sessionId ? terms.filter((t: any) => (t.session_id ?? t.academic_session_id) === Number(sessionId)) : terms,
    [terms, sessionId]
  );

  useEffect(() => {
    if (!sessionId && sessions.length > 0) {
      const cur = sessions.find((s: any) => s.is_current) ?? sessions[0];
      setSessionId(cur.id);
    }
  }, [sessions, sessionId]);

  useEffect(() => {
    if (!termId && visibleTerms.length > 0) {
      const cur = visibleTerms.find((t: any) => t.is_current);
      if (cur) setTermId(cur.id);
    }
    if (termId && !visibleTerms.some((t: any) => t.id === termId)) setTermId('');
  }, [visibleTerms, termId]);

  const enabled = !!classId && !!termId;

  const resultsQ = useQuery({
    queryKey: ['publish-results', classId, termId],
    queryFn: () => getClassResults(Number(classId), { term_id: Number(termId) }).then(unwrap),
    enabled,
  });

  const data = resultsQ.data;
  const rows = arr<any>(data?.results);
  const missing = arr<any>(data?.without_result);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r: any) => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);

  const computeM = useMutation({
    mutationFn: () => computeResults({ class_id: Number(classId), term_id: Number(termId) }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setLastResult({ stage: 'compute', data: d });
      qc.invalidateQueries({ queryKey: ['publish-results', classId, termId] });
      setError(null);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Compute failed'),
  });

  const approveM = useMutation({
    mutationFn: () => approveResults({ class_id: Number(classId), term_id: Number(termId) }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setLastResult({ stage: 'approve', data: d });
      qc.invalidateQueries({ queryKey: ['publish-results', classId, termId] });
      setError(null);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Approve failed'),
  });

  const publishM = useMutation({
    mutationFn: () => publishResults({ class_id: Number(classId), term_id: Number(termId) }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setLastResult({ stage: 'publish', data: d });
      qc.invalidateQueries({ queryKey: ['publish-results', classId, termId] });
      setError(null);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Publish failed'),
  });

  const totalResults = rows.length;
  const computedCount = counts.computed ?? 0;
  const approvedCount = counts.approved ?? 0;
  const publishedCount = counts.published ?? 0;
  const heldCount = counts.held ?? 0;

  const canCompute = !!data && (totalResults === 0 || missing.length > 0 || Object.keys(counts).some(k => k === 'draft'));
  const canApprove = computedCount > 0;
  const canPublish = approvedCount > 0;

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Assessments & Results</p>
        <h1 className="text-3xl font-extrabold text-slate-900">Publish results</h1>
        <p className="text-slate-500 text-sm mt-1">
          Three stages: compute → approve → publish. Parents and students get notified on publish.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
            <select value={sessionId} onChange={(e) => { setSessionId(e.target.value ? Number(e.target.value) : ''); setTermId(''); }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose session…</option>
              {sessions.map((s: any) => <option key={s.id} value={s.id}>{s.name}{s.is_current ? ' (active)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
              disabled={!sessionId}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
              <option value="">Choose term…</option>
              {visibleTerms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          Pick a session, term, and class to begin.
        </div>
      )}

      {enabled && resultsQ.isLoading && (
        <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
      )}

      {enabled && data && (
        <>
          {/* Progress stages */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-2">
              <StageChip
                icon={<Calculator className="w-4 h-4" />}
                number={1}
                label="Compute"
                count={totalResults}
                subtext={totalResults === 0 ? 'Not computed yet' : 'Draft + computed'}
                active={canCompute}
                done={totalResults > 0}
              />
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <StageChip
                icon={<ShieldCheck className="w-4 h-4" />}
                number={2}
                label="Approve"
                count={approvedCount + publishedCount + heldCount}
                subtext={`${computedCount} pending`}
                active={canApprove}
                done={computedCount === 0 && approvedCount + publishedCount + heldCount > 0}
              />
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <StageChip
                icon={<Send className="w-4 h-4" />}
                number={3}
                label="Publish"
                count={publishedCount + heldCount}
                subtext={`${approvedCount} pending · ${heldCount} held`}
                active={canPublish}
                done={approvedCount === 0 && publishedCount + heldCount > 0}
              />
            </div>
          </div>

          {/* Status summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Students" value={rows.length} />
            <StatCard label="Computed" value={computedCount} tone="blue" />
            <StatCard label="Approved" value={approvedCount} tone="violet" />
            <StatCard label="Published" value={publishedCount + heldCount} tone="green" />
          </div>

          {/* Missing results */}
          {missing.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
              <p className="font-semibold">
                {missing.length} student(s) have no result yet
              </p>
              <p className="text-xs mt-1">
                They don’t hold the others back: you can approve and publish the rest now. When their scores are in Score Entry, press Compute again: only they are added (published results stay), then approve and publish them.
              </p>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-semibold underline">Show names</summary>
                <ul className="mt-1 text-xs list-disc list-inside">
                  {missing.map((s: any) => (
                    <li key={s.student_id}>{s.name}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          {/* Action buttons per stage */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ActionCard
              title="1. Compute results"
              description="Calculates totals, grades, and positions from scores"
              icon={<Calculator className="w-5 h-5" />}
              tone="blue"
              disabled={computeM.isPending || !enabled}
              busy={computeM.isPending}
              onClick={async () => {
                if (totalResults > 0 && !(await askConfirm(publishedCount + heldCount > 0
                  ? 'Compute the students who are not published yet? Published results keep their scores; class positions are updated to include everyone.'
                  : 'Re-compute this class? Existing computed results will be overwritten.'))) return;
                computeM.mutate();
              }}
              buttonLabel="Compute"
            />

            <ActionCard
              title="2. Approve"
              description="Desk officer / owner signs off. Locks scores from further edits."
              icon={<ShieldCheck className="w-5 h-5" />}
              tone="violet"
              disabled={approveM.isPending || !canApprove}
              busy={approveM.isPending}
              onClick={async () => {
                if (!(await askConfirm(`${computedCount} result(s) will be approved. Parents cannot see them until published.`))) return;
                approveM.mutate();
              }}
              buttonLabel="Approve"
              badge={computedCount > 0 ? `${computedCount} pending` : undefined}
            />

            <ActionCard
              title="3. Publish"
              description="Makes results visible to parents and students. Sends notifications."
              icon={<Send className="w-5 h-5" />}
              tone="green"
              disabled={publishM.isPending || !canPublish}
              busy={publishM.isPending}
              onClick={async () => {
                if (!(await askConfirm(`Publish ${approvedCount} result(s)? Parents and students will be notified and can see results immediately.`))) return;
                publishM.mutate();
              }}
              buttonLabel="Publish"
              badge={approvedCount > 0 ? `${approvedCount} ready` : undefined}
              warning="This sends notifications to parents and students"
            />
          </div>

          {/* Last result banner */}
          {lastResult && (
            <div className={`rounded-xl border p-4 text-sm flex items-start gap-2 ${
              lastResult.stage === 'compute' ? 'bg-blue-50 border-blue-200 text-blue-900' :
              lastResult.stage === 'approve' ? 'bg-violet-50 border-violet-200 text-violet-900' :
              'bg-green-50 border-green-200 text-green-900'
            }`}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                {lastResult.stage === 'compute' && (
                  <>
                    <b>Computed {lastResult.data?.students ?? 0} student(s).</b>
                    {lastResult.data?.incomplete > 0 && (
                      <span> {lastResult.data.incomplete} subject result(s) have missing scores — check Score Entry.</span>
                    )}
                  </>
                )}
                {lastResult.stage === 'approve' && (
                  <b>Approved {lastResult.data?.approved ?? 0} result(s).</b>
                )}
                {lastResult.stage === 'publish' && (
                  <>
                    <b>Published {lastResult.data?.published ?? 0} result(s).</b>
                    {lastResult.data?.held > 0 && (
                      <span> {lastResult.data.held} held until fees cleared (they don't see it yet).</span>
                    )}
                    <span> Parents and students notified.</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Results preview */}
          {rows.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                <p className="font-bold text-slate-900">
                  {data?.class} · {data?.term}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(counts).map(([status, count]) => (
                    <span key={status} className={`px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone(status)}`}>
                      {count} {status}
                    </span>
                  ))}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">#</th>
                    <th className="px-3 py-2 text-left">Student</th>
                    <th className="px-3 py-2 text-left">Adm. No.</th>
                    <th className="px-3 py-2 text-right">Average</th>
                    <th className="px-3 py-2 text-right">Position</th>
                    <th className="px-3 py-2 text-center">Grade</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r: any, idx: number) => (
                    <tr key={r.result_id} className={r.held ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.admission_no}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.average != null ? Number(r.average).toFixed(1) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.position ?? '—'}</td>
                      <td className="px-3 py-2 text-center font-bold">{r.grade ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone(r.status)}`}>
                          {r.status}
                        </span>
                        {r.hold_reason && (
                          <p className="text-[10px] text-amber-700 mt-0.5">{r.hold_reason}</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StageChip({ icon, number, label, count, subtext, active, done }: {
  icon: React.ReactNode;
  number: number;
  label: string;
  count: number;
  subtext?: string;
  active?: boolean;
  done?: boolean;
}) {
  return (
    <div className="flex-1 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        done ? 'bg-green-100 text-green-700' :
        active ? 'bg-slate-900 text-white' :
        'bg-slate-100 text-slate-400'
      }`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-bold ${done ? 'text-green-700' : active ? 'text-slate-900' : 'text-slate-500'}`}>
          {number}. {label}
        </p>
        <p className="text-xs text-slate-500">
          {count} result{count === 1 ? '' : 's'}{subtext && ` · ${subtext}`}
        </p>
      </div>
    </div>
  );
}

function ActionCard({ title, description, icon, tone, disabled, busy, onClick, buttonLabel, badge, warning }: {
  title: string;
  description: string;
  icon: React.ReactNode;
  tone: 'blue' | 'violet' | 'green';
  disabled: boolean;
  busy: boolean;
  onClick: () => void;
  buttonLabel: string;
  badge?: string;
  warning?: string;
}) {
  const tones = {
    blue: { icon: 'bg-blue-100 text-blue-700', button: 'bg-blue-600 hover:bg-blue-700 text-white' },
    violet: { icon: 'bg-violet-100 text-violet-700', button: 'bg-violet-600 hover:bg-violet-700 text-white' },
    green: { icon: 'bg-green-100 text-green-700', button: 'bg-green-600 hover:bg-green-700 text-white' },
  };
  const t = tones[tone];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.icon}`}>
        {icon}
      </div>
      <div>
        <p className="font-bold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
        {warning && (
          <p className="text-xs text-amber-700 mt-1">⚠ {warning}</p>
        )}
      </div>
      <button onClick={onClick} disabled={disabled}
        className={`w-full py-2 rounded-lg text-sm font-semibold ${t.button} disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {busy ? 'Processing…' : buttonLabel}
      </button>
      {badge && (
        <p className="text-xs text-center text-slate-500">{badge}</p>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: any; tone?: 'blue' | 'violet' | 'green' }) {
  const tones = {
    blue: 'text-blue-700',
    violet: 'text-violet-700',
    green: 'text-green-700',
  };
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${tone ? tones[tone] : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
